import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { db } from '@core/db/db'
import { money } from '@core/utils/format'
import { httpErrorText } from '@core/utils/httpError'
import { exportWorkbook } from '@utils/excel/ExcelExport'
import { btn, btnBase, btnSize, cx, hint, table, tableWrap, td, tdNum, th, thNum } from '@ui/classes'
import {
  credInput,
  errBanner,
  filterBar,
  okBanner,
  seg,
  segActive,
  segBar,
  segIdle,
  sectionCard
} from '@ui/admin'
import { CLOUD_API, CLOUD_NOT_CONNECTED, deviceBadge, paymentLabel } from './shared'
import { useAuthToken } from './useAuthToken'

/** Разрез отчёта (ARCH-TASK-63). */
type Dimension =
  | 'day'
  | 'item'
  | 'category'
  | 'payment'
  | 'cashier'
  | 'device'
  | 'marking'
type Period = 'today' | 'week' | 'month' | 'custom'

interface ReportColumn {
  key: string
  title: string
  type: 'text' | 'number' | 'money' | 'percent'
  align?: 'left' | 'right'
  width?: number
}

interface Report {
  dimension: Dimension
  range: { from: string; to: string; timezone: string; zone_id: string | null; status: string }
  columns: ReportColumn[]
  rows: Record<string, unknown>[]
  totals: {
    revenue: number
    /** Выручка продаж и сумма возвратов (ARCH-TASK-65). */
    sales_revenue: number
    returns_total: number
    returns_count: number
    receipts: number
    avg_check: number
    items: number
    rows: number
  }
  total_rows: number
  limit: number
  offset: number
}

const DIMENSIONS: Dimension[] = [
  'day',
  'item',
  'category',
  'payment',
  'cashier',
  'device',
  'marking'
]

const DIMENSION_LABEL: Record<Dimension, string> = {
  day: 'По дням',
  item: 'По товарам',
  category: 'По категориям',
  payment: 'По оплате',
  cashier: 'По кассирам',
  device: 'По кассам',
  marking: 'Маркировка'
}

const PERIOD_LABEL: Record<Period, string> = {
  today: 'Сегодня',
  week: '7 дней',
  month: '30 дней',
  custom: 'Свой период'
}

/** Сколько строк грузим за раз (и сколько добавляет «Показать ещё»). */
const PAGE_SIZE = 200

/** Ключи meta: последний выбранный разрез и период (переживают перезапуск). */
const META_REPORTS_DIMENSION = 'reportsDimension'
const META_REPORTS_PERIOD = 'reportsPeriod'

/** Период отчёта: пресеты или произвольные даты (локальные сутки). */
function rangeFor(
  period: Period,
  customFrom: string,
  customTo: string
): { from: Date; to: Date } | null {
  const now = new Date()
  if (period === 'custom') {
    if (!customFrom || !customTo) return null
    const from = new Date(`${customFrom}T00:00:00`)
    const to = new Date(`${customTo}T23:59:59`)
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null
    return { from, to }
  }
  if (period === 'today') {
    const from = new Date(now)
    from.setHours(0, 0, 0, 0)
    return { from, to: now }
  }
  const days = period === 'week' ? 7 : 30
  const from = new Date(now)
  from.setHours(0, 0, 0, 0)
  from.setDate(from.getDate() - (days - 1))
  return { from, to: now }
}

/** 'YYYY-MM-DD' → 'dd.mm.yyyy' без UTC-сдвига. */
function dayText(value: string): string {
  const [year, month, date] = value.split('-')
  if (!year || !month || !date) return value
  return `${date}.${month}.${year}`
}

/** Значение для экрана. */
function formatCell(column: ReportColumn, value: unknown): string {
  if (column.type === 'money') return money(Number(value))
  if (column.type === 'percent') return `${Number(value).toFixed(1)} %`
  if (column.type === 'number') {
    return Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })
  }
  if (column.key === 'method') return paymentLabel(String(value))
  if (column.key === 'device_id') {
    // short обязателен: у двух мобильных касс подпись одинаковая.
    const badge = deviceBadge(String(value))
    return `${badge.label} ${badge.short}`
  }
  if (column.key === 'day') return dayText(String(value))
  return String(value ?? '—')
}

/** Значение для Excel: числа остаются числами, подписи — человеческие. */
function rawCell(column: ReportColumn, value: unknown): string | number {
  return column.type === 'text' ? formatCell(column, value) : Number(value)
}

/** Итог по колонке для строки «ИТОГО». */
function totalsCell(key: string, totals: Report['totals']): string | number | null {
  if (key === 'revenue') return totals.revenue
  if (key === 'sales_revenue') return totals.sales_revenue
  if (key === 'returns_total') return totals.returns_total
  if (key === 'returns_count') return totals.returns_count
  if (key === 'receipts') return totals.receipts
  if (key === 'avg_check') return totals.avg_check
  if (key === 'items') return totals.items
  if (key === 'share') return 100
  return null
}

/** Человеческий текст ошибки отчёта по коду ответа. */
function reportError(code: string | undefined, status: number): string {
  if (code === 'invalid_dimension') return 'Неизвестный разрез отчёта'
  if (code === 'range_too_large') return 'Период слишком длинный — максимум 92 дня'
  if (code === 'to must be after from') return 'Дата начала должна быть раньше даты окончания'
  return httpErrorText('Отчёт', status)
}

/**
 * «Отчёты» — шесть разрезов продаж с выгрузкой в Excel (ARCH-TASK-63).
 *
 * Данные приходят табличным контрактом (`columns` + `rows` + `totals`):
 * экран и файл рендерятся из одного описания, поэтому шапки не разъезжаются.
 */
export function ReportsSection() {
  const token = useAuthToken()
  const [dimension, setDimension] = useState<Dimension>('day')
  const [period, setPeriod] = useState<Period>('week')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [fiscalOnly, setFiscalOnly] = useState(false)
  const [limit, setLimit] = useState(PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null)
  const [data, setData] = useState<Report | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  /** Настройки из meta прочитаны — только тогда делаем первый запрос. */
  const [metaLoaded, setMetaLoaded] = useState(false)
  /** Номер последнего запроса: ответы предыдущего разреза не должны перебивать. */
  const requestRef = useRef(0)

  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', [])

  // Восстанавливаем последний разрез и период (ARCH-TASK-63.1).
  useEffect(() => {
    void (async () => {
      try {
        const [savedDimension, savedPeriod] = await Promise.all([
          db.meta.get(META_REPORTS_DIMENSION),
          db.meta.get(META_REPORTS_PERIOD)
        ])
        if (
          typeof savedDimension?.value === 'string' &&
          DIMENSIONS.includes(savedDimension.value as Dimension)
        ) {
          setDimension(savedDimension.value as Dimension)
        }
        if (
          typeof savedPeriod?.value === 'string' &&
          Object.prototype.hasOwnProperty.call(PERIOD_LABEL, savedPeriod.value)
        ) {
          setPeriod(savedPeriod.value as Period)
        }
      } catch {
        // Не смогли прочитать meta — остаёмся на дефолтах.
      } finally {
        setMetaLoaded(true)
      }
    })()
  }, [])

  /** Выбрать разрез и запомнить выбор. */
  const selectDimension = (key: Dimension) => {
    setDimension(key)
    resetView()
    void db.meta.put({ key: META_REPORTS_DIMENSION, value: key }).catch(() => undefined)
  }

  /** Выбрать период и запомнить выбор. */
  const selectPeriod = (key: Period) => {
    setPeriod(key)
    resetView()
    void db.meta.put({ key: META_REPORTS_PERIOD, value: key }).catch(() => undefined)
  }

  /** Параметры запроса для текущего разреза/периода. */
  const buildQuery = useCallback(
    (maxRows: number): string | null => {
      const range = rangeFor(period, customFrom, customTo)
      if (!range) return null
      return new URLSearchParams({
        dimension,
        from: range.from.toISOString(),
        to: range.to.toISOString(),
        tz: timezone,
        limit: String(maxRows),
        offset: '0',
        status: fiscalOnly ? 'fiscal' : 'all'
      }).toString()
    },
    [dimension, fiscalOnly, period, customFrom, customTo, timezone]
  )

  const load = useCallback(async () => {
    // Ждём восстановления настроек — иначе первый запрос уйдёт с дефолтами.
    if (!metaLoaded) return
    if (!token) {
      setData(null)
      return
    }
    const query = buildQuery(limit)
    if (!query) {
      setData(null)
      return
    }
    const requestId = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${CLOUD_API}/analytics/report?${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null
        throw new Error(reportError(payload?.error, response.status))
      }
      const report = (await response.json()) as Report
      if (requestId !== requestRef.current) return
      setData(report)
    } catch (e: unknown) {
      if (requestId !== requestRef.current) return
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      if (requestId === requestRef.current) setLoading(false)
    }
  }, [token, buildQuery, limit, metaLoaded])

  useEffect(() => {
    void load()
  }, [load])

  /** Новая выборка — снова первые 200 строк, без сортировки и поиска. */
  const resetView = useCallback(() => {
    setLimit(PAGE_SIZE)
    setSort(null)
    setSearch('')
    setNote(null)
  }, [])

  /** Строки с учётом поиска и сортировки (по загруженным данным). */
  const rows = useMemo(() => {
    if (!data) return []
    const query = search.trim().toLowerCase()
    let list = data.rows
    if (query) {
      list = list.filter((row) =>
        Object.values(row).some((value) => String(value).toLowerCase().includes(query))
      )
    }
    if (sort) {
      const { key, dir } = sort
      list = [...list].sort((a, b) => {
        const left = a[key]
        const right = b[key]
        if (typeof left === 'number' && typeof right === 'number') return (left - right) * dir
        return String(left).localeCompare(String(right), 'ru') * dir
      })
    }
    return list
  }, [data, search, sort])

  const toggleSort = (key: string) => {
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: -1 }
    )
  }

  /** Выгрузка в Excel: всегда полная (до 20 000 строк), а не только видимая страница. */
  const exportExcel = async () => {
    if (!token) return
    const query = buildQuery(20000)
    if (!query) {
      setError('Укажите обе даты периода')
      return
    }
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const response = await fetch(`${CLOUD_API}/analytics/report?${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null
        throw new Error(reportError(payload?.error, response.status))
      }
      const full = (await response.json()) as Report
      const range = rangeFor(period, customFrom, customTo)
      const stamp = (date: Date) => date.toISOString().slice(0, 10)
      const fileName = `otchet_${dimension}_${range ? stamp(range.from) : 'from'}_${
        range ? stamp(range.to) : 'to'
      }.xlsx`

      const result = await exportWorkbook(fileName, [
        {
          name: DIMENSION_LABEL[dimension],
          columns: full.columns.map((column) => ({ title: column.title, width: column.width })),
          rows: full.rows.map((row) => full.columns.map((column) => rawCell(column, row[column.key]))),
          totals: [
            'ИТОГО',
            ...full.columns.slice(1).map((column) => totalsCell(column.key, full.totals))
          ]
        }
      ])

      setNote(
        result === 'saved-to-documents'
          ? `Файл ${fileName} сохранён в «Документах» приложения`
          : `Файл ${fileName} скачан (${full.rows.length} строк)`
      )
      if (full.total_rows > full.rows.length) {
        setNote((prev) => `${prev ?? ''} · выгружены первые ${full.rows.length} из ${full.total_rows}`)
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }



  const totals = data?.totals
  const hasMore = data != null && data.rows.length < data.total_rows
  const isSearchable =
    dimension === 'item' || dimension === 'category' || dimension === 'marking'

  return (
    <div className={sectionCard}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-big font-bold">Отчёты</h3>
        <button
          onClick={() => void exportExcel()}
          disabled={!token || busy || !data || data.rows.length === 0}
          className={cx(btnBase, btn.secondary, btnSize.sm)}
        >
          {busy ? 'Готовим файл…' : 'Выгрузить Excel'}
        </button>
      </div>

      <div className={segBar} role="tablist" aria-label="Период отчёта">
        {(Object.keys(PERIOD_LABEL) as Period[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={period === key}
            onClick={() => selectPeriod(key)}
            className={cx(seg, period === key ? segActive : segIdle)}
          >
            {PERIOD_LABEL[key]}
          </button>
        ))}
      </div>

      {period === 'custom' && (
        <div className={filterBar}>
          <label className="flex items-center gap-1.5 text-small">
            с
            <input
              type="date"
              value={customFrom}
              onChange={(e) => {
                setCustomFrom(e.target.value)
                resetView()
              }}
              className={credInput}
            />
          </label>
          <label className="flex items-center gap-1.5 text-small">
            по
            <input
              type="date"
              value={customTo}
              onChange={(e) => {
                setCustomTo(e.target.value)
                resetView()
              }}
              className={credInput}
            />
          </label>
        </div>
      )}

      <div className={segBar} role="tablist" aria-label="Разрез отчёта">
        {DIMENSIONS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={dimension === key}
            onClick={() => selectDimension(key)}
            className={cx(seg, dimension === key ? segActive : segIdle)}
          >
            {DIMENSION_LABEL[key]}
          </button>
        ))}
      </div>

      <div className={filterBar}>
        {isSearchable && (
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по загруженным строкам"
            className={cx(credInput, 'min-w-[220px]')}
          />
        )}
        <label className="flex cursor-pointer items-center gap-1.5 text-small">
          <input
            type="checkbox"
            checked={fiscalOnly}
            onChange={(e) => {
              setFiscalOnly(e.target.checked)
              resetView()
            }}
            className="h-4 w-4 cursor-pointer accent-ok"
          />
          Только фискальные чеки
        </label>
      </div>

      {note && <p className={okBanner}>{note}</p>}
      {error && <p className={errBanner}>{error}</p>}
      {!token && <p className={hint}>{CLOUD_NOT_CONNECTED}</p>}
      {token && loading && <p className={hint}>Считаем отчёт…</p>}
      {token && !loading && !error && data == null && (
        <p className={hint}>Укажите обе даты периода.</p>
      )}
      {token && !loading && data != null && data.rows.length === 0 && (
        <p className={hint}>За период продаж нет.</p>
      )}


      {data && data.rows.length > 0 && (
        <>
          <div className={tableWrap}>
            <table className={table}>
              <thead>
                <tr>
                  {data.columns.map((column) => (
                    <th
                      key={column.key}
                      className={cx(
                        column.align === 'right' ? thNum : th,
                        'cursor-pointer select-none'
                      )}
                      onClick={() => toggleSort(column.key)}
                      title="Сортировать"
                    >
                      {column.title}
                      {sort?.key === column.key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    {data.columns.map((column) => (
                      <td
                        key={column.key}
                        className={column.align === 'right' ? cx(tdNum, 'whitespace-nowrap') : td}
                      >
                        {formatCell(column, row[column.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr>
                    {data.columns.map((column, index) => {
                      const value = index === 0 ? 'ИТОГО' : totalsCell(column.key, totals)
                      return (
                        <td
                          key={column.key}
                          className={cx(
                            column.align === 'right' ? tdNum : td,
                            'whitespace-nowrap font-semibold'
                          )}
                        >
                          {typeof value === 'string'
                            ? value
                            : value == null
                              ? '—'
                              : formatCell(column, value)}
                        </td>
                      )
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className={hint}>
              Показано {rows.length} из {data.total_rows}
              {search.trim() ? ' (с учётом поиска)' : ''}
            </span>
            {hasMore && (
              <button
                onClick={() => setLimit((value) => value + PAGE_SIZE)}
                disabled={loading}
                className={cx(btnBase, btn.secondary, btnSize.sm)}
              >
                Показать ещё
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

